/**
 * Client-side mirror of the cleaner-side pre-flight checks the server
 * enforces before allowing complete-task → submit. The server is still
 * authoritative — this gives the cleaner instant feedback so the submit
 * button is meaningfully disabled instead of failing on the network.
 *
 * Stays in sync with:
 *   supabase/functions/complete-task/index.ts (submit branch)
 *   supabase/migrations/20260501000004_functions.sql → check_room_ready_preconditions
 */

export interface ChecklistItemPreflight {
  is_required:        boolean
  completed:          boolean
  /**
   * PROJECT_STATE task #72 — when true, the item is not applicable
   * to this room (no recycling bin, no vent-a-hood, etc.) and is
   * treated as "done" for the required-item count. Mirrors the
   * server's `check_room_ready_preconditions` CHECK 1 clause:
   *   completed = false AND is_not_applicable = false
   */
  is_not_applicable:  boolean
}

export interface PhotoRequirementPreflight {
  category:    string
  is_required: boolean
}

export interface ExistingPhotoPreflight {
  photo_category: string
}

export interface PreflightArgs {
  taskStatus:         string
  requiredItems:      readonly ChecklistItemPreflight[]
  photoRequirements:  readonly PhotoRequirementPreflight[]
  existingPhotos:     readonly ExistingPhotoPreflight[]
}

/**
 * Returns null when nothing is blocking submission.
 * Returns a human-readable string describing the block when something is.
 * The string is rendered inline (CLAUDE.md ERROR 4) — never toasted.
 */
export function computeLocalBlock(a: PreflightArgs): string | null {
  if (a.taskStatus !== 'in_progress') {
    return `Task status is "${a.taskStatus.replace(/_/g, ' ')}" — only in-progress tasks can be submitted.`
  }

  const incomplete = a.requiredItems.filter((i) => i.is_required && !i.completed && !i.is_not_applicable).length
  if (incomplete > 0) {
    return `${incomplete} required checklist item${incomplete === 1 ? '' : 's'} still need to be completed.`
  }

  const requiredCats = a.photoRequirements.filter((p) => p.is_required).map((p) => p.category)
  const haveByCat    = new Set(a.existingPhotos.map((p) => p.photo_category))
  const missing      = requiredCats.filter((c) => !haveByCat.has(c))
  if (missing.length > 0) {
    return `Missing required photo${missing.length === 1 ? '' : 's'}: ${missing.map((c) => c.replace(/_/g, ' ')).join(', ')}.`
  }

  return null
}
